import { useEffect, useMemo } from "react";

import { WelcomeRewardModal } from "@/components/wallet/modals/WelcomeRewardModal";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";
import type { NotificationsModels } from "@/data/notifications/notifications.models";
import { NotificationsQueries } from "@/data/notifications/notifications.queries";
import { useWelcomeRewardModalStore } from "@/providers/wallet/wallet.store";

const isCashbackNotification = (type: string, data: NotificationsModels.NotificationDataDto) => {
  return type === "CashbackAvailableNotification" && data.category === "wallet" && data.type === "cashback_available";
};

export const GlobalWelcomeRewardModal = () => {
  const { isLoggedIn, user } = AuthContext.useAuth();
  const { showWelcomeRewardModal, setShowWelcomeRewardModal } = useWelcomeRewardModalStore();

  const isOnboardingCompleted = user?.onboardingStep === CommonModels.OnboardingStepEnum.completed;

  // Fetch unread notifications when logged in and onboarding is completed
  const { data: unreadNotifications } = NotificationsQueries.useGetUnread({
    enabled: isLoggedIn && isOnboardingCompleted,
  });

  // Find unread cashback notification
  const pendingCashbackNotification = useMemo(() => {
    if (!unreadNotifications) return undefined;
    return unreadNotifications.find((n) => isCashbackNotification(n.type, n.data) && !n.readAt);
  }, [unreadNotifications]);

  // Mark notification as read mutation
  const { mutate: markAsRead } = NotificationsQueries.useMarkAsRead();

  // Show modal if pending notification exists
  useEffect(() => {
    if (pendingCashbackNotification) {
      setShowWelcomeRewardModal(true);
    }
  }, [pendingCashbackNotification, setShowWelcomeRewardModal]);

  // Handle modal close - mark notification as read if it was a pending one
  const handleClose = () => {
    if (pendingCashbackNotification) {
      markAsRead({ id: pendingCashbackNotification.id });
    }
    setShowWelcomeRewardModal(false);
  };

  if (!isLoggedIn || !isOnboardingCompleted) {
    return null;
  }

  return (
    <WelcomeRewardModal
      isOpen={showWelcomeRewardModal}
      onClose={handleClose}
    />
  );
};
